import React, { useEffect, useState, useContext } from 'react'
import { useLoaderData, useParams } from 'react-router-dom'
import { useAuth } from '../../../../../hooks/auth'

import {
    CurrencyRate,
    Purchase,
    PurchaseLot,
    PurchaseLotItem,
} from '../../types'

import { CurrencyList } from '../../../Plans/Positions/Edit/types'

import Footer from './components/Footer'

import { Structure } from '../../../Org/Structure/types'
import { RegionRf } from './types'

import { User } from '../../../Org/Users/types'
import { OrganizationInfo } from '../../../Org/Home/types'

import { ThemeContext } from '../../../../../components/ThemeContext'

import PurchaseLotPositionInfo from './components/PurchaseLotPositionInfo'

import { CountryList } from '../types'
import PurchaseStage from '../components/PurchaseStage'
import PurchaseInfo from '../components/PurchaseInfo'

type ApiResponseRoleGuid = {
    result: QueryItem[]
}

type QueryItem = {
    guid: string
    name: string
    organization_guid: string
    created_at: string
    updated_at: string
}

export const loadPurchaseLotData = async ({
    params,
}: any): Promise<PurchaseLot | null> => {
    const action = params.action
    const purchase_reg_number = params.purchase_reg_number
    const purchase_action = params.purchase_action
    const purchase_guid = params.purchase_guid
    const purchase_lot_guid = params.purchase_lot_guid

    // Для create — ничего не грузим
    if (!purchase_guid && !purchase_lot_guid) return null

    const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases/lots/${purchase_lot_guid}?purchase_guid=${purchase_guid}`

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
        // пусть отрисуется ErrorPage
        throw new Response('Failed to fetch purchase', {
            status: response.status,
        })
    }

    const data = await response.json()

    // API может вернуть либо объект, либо массив с одним объектом
    const purchase_lot: PurchaseLot | null = Array.isArray(data)
        ? (data[0] ?? null)
        : (data ?? null)

    // Доп. проверка, что это действительно контракт
    if (!purchase_lot || !('guid' in purchase_lot)) return null

    return purchase_lot
}

const getStructureList = async (): Promise<Structure[]> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/organization_struct',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на способы закупок')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getRole = async (role_guid: string): Promise<QueryItem> => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/organizations/roles_by_guid/${role_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка запроса на роль')
        }

        const data: ApiResponseRoleGuid = await response.json()
        // Извлекаем первый элемент массива, т.к. он всегда содержит единственный объект
        return data.result[0]
    } catch (error) {
        console.error('Ошибка:', error)
        // Возвращаем объект по умолчанию, соответствующий типу QueryItem
        return {
            guid: '',
            name: '',
            organization_guid: '',
            created_at: '',
            updated_at: '',
        }
    }
}

const PurchaseLotEditPage: React.FC = () => {
    const loadedPurchaseLot = useLoaderData() as PurchaseLot

    const [purchaseLot, setPurchaseLot] =
        useState<PurchaseLot>(loadedPurchaseLot)
    const [purchase, setPurchase] = useState<Purchase>()
    const [purchaseLoading, setPurchaseLoading] = useState(false)

    const [purchaseLotItems, setPurchaseLotItems] = useState<PurchaseLotItem[]>(
        []
    )
    const [purchaseLotItemsLoading, setPurchaseLotItemsLoading] =
        useState(false)

    const [orgData, setOrgData] = useState<OrganizationInfo>()

    const { user } = useAuth()
    const { theme } = useContext(ThemeContext)

    const { purchase_reg_number: purchase_reg_number } = useParams()
    const { purchase_guid: purchase_guid } = useParams()
    const { purchase_lot_guid: purchase_lot_guid } = useParams()
    const { purchase_action: purchase_action } = useParams()

    const [isChangingCurrency, setChangingCurrency] = useState(false)

    const [isUserRoleName, setUserRoleName] = useState('')
    const [structList, setStructList] = useState<Structure[]>([])
    const [currencyList, setCurrencyList] = useState<CurrencyList[]>([])
    const [currencyRate, setCurrencyRate] = useState<CurrencyRate>()
    const [oksmList, setOksmList] = useState<CountryList[]>([])
    const [regionRfList, setRegionRfList] = useState<RegionRf[]>([])

    const [isUserStructGuid, setUserStructGuid] = useState('')
    const [isUserStructName, setUserStructName] = useState('')

    const [purchasesLoading, setPurchasesLoading] = useState(false)
    const [purchasesError, setPurchasesError] = useState<string | null>(null)

    // сбрасываем кеш при смене договора
    useEffect(() => {
        setPurchaseLot(loadedPurchaseLot ?? null)
        // тут же можно сбрасывать связанные состояния
    }, [loadedPurchaseLot?.guid])

    const loadPurchaseLotItems = async (
        purchaseLotGuid?: string
    ): Promise<PurchaseLotItem[]> => {
        setPurchaseLotItemsLoading(true)
        let purchase_lot_guid_for_q

        if (!purchase_guid && !purchase_lot_guid) return []

        if (!purchaseLotGuid) {
            purchase_lot_guid_for_q = purchase_lot_guid
        } else {
            purchase_lot_guid_for_q = purchaseLotGuid
        }

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases/lots/items?purchase_guid=${purchase_guid}&lot_guid=${purchase_lot_guid_for_q}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка загрузки позиций лота')
        }

        const data = await response.json()
        setPurchaseLotItems(Array.isArray(data) ? data : [])
        setPurchaseLotItemsLoading(false)
        return Array.isArray(data) ? data : []
    }

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setOrgData(data)
                    return data
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getCurrencyList = async (): Promise<CurrencyList[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/currency',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на курсы валют')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getOksmList = async (): Promise<CountryList[]> => {
        return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/oksm', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на Страны')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getRegionRfList = async (): Promise<RegionRf[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/region_rf',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на регионы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const loadPurchaseData = async (
        purchaseGuid: string
    ): Promise<Purchase> => {
        setPurchaseLoading(true)

        return fetch(
            process.env.REACT_APP_API_URL +
                '/api/backend/v1/purchases/' +
                purchaseGuid,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setPurchase(data)
                    return data
                } else {
                    throw new Error('Failed to fetch purchase data')
                }
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => setPurchaseLoading(true))
    }

    useEffect(() => {
        if (purchaseLot?.guid) {
            loadPurchaseLotItems(purchaseLot.guid)

            loadPurchaseData(purchase_guid || purchaseLot.purchase_guid)
        }
    }, [purchaseLot?.guid])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const organization = await loadOrganizationData()
                if (organization) {
                    setOrgData(organization)
                }

                const role = await getRole(user?.object.role_id)
                setUserRoleName(role.name)

                const structs = await getStructureList()
                if (Array.isArray(structs)) {
                    setStructList(structs)
                    if (structs) {
                        const matchingStruct = structs.find(
                            (struct) =>
                                struct.guid.toLowerCase() ===
                                user?.object.struct_id
                        )

                        const structShortName =
                            matchingStruct?.struct_short_name || ''
                        setUserStructName(structShortName)
                        const structGuid = matchingStruct?.guid || ''
                        setUserStructGuid(structGuid)

                        // Только для создания — проставляем дефолт
                        // if (action === 'create') {
                        //     setPurchaseLot((prev) => ({
                        //         ...prev,
                        //         struct_guid: prev.struct_guid ?? structGuid,
                        //         struct_name:
                        //             prev.struct_name ?? structShortName,
                        //     }))
                        // }
                    }
                } else {
                    console.error('Нет данных:', structs)
                }

                const currency = await getCurrencyList()
                if (Array.isArray(currency)) {
                    setCurrencyList(currency)
                } else {
                    console.error('Нет данных:', currency)
                }

                const oksm = await getOksmList()
                if (Array.isArray(oksm)) {
                    setOksmList(oksm)
                } else {
                    console.error('Нет данных:', oksm)
                }

                const regionrf = await getRegionRfList()
                if (Array.isArray(regionrf)) {
                    setRegionRfList(regionrf)
                } else {
                    console.error('Нет данных:', regionrf)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    return (
        <div className=" flex flex-col w-full h-screen items-start justify-start ">
            <div className="flex flex-col w-full h-fit items-center justify-center">
                <div className="flex flex-col w-full h-[90px] items-center justify-center bg-[#e5e5e5] dark:bg-[#000000]">
                    <PurchaseStage
                        purchase={purchase ?? ({} as Purchase)}
                        theme={theme}
                    />
                </div>
                <div className="flex flex-col w-full min-h-[100px] items-center justify-center bg-[#ebebeb] dark:bg-[#111111]">
                    <PurchaseInfo
                        purchase={purchase ?? ({} as Purchase)}
                        theme={theme}
                        currencyList={currencyList}
                    />
                </div>
            </div>

            <div className="grow flex flex-row w-full h-[calc(100vh_-_250px)] items-center justify-center gap-5 px-5 py-5 bg-[#f0f3f5] dark:bg-[#181818] ">
                <div className="flex flex-col min-w-[1270px] max-w-[1270px] h-full min-h-0 gap-y-12 items-start justify-start overflow-x-visible overflow-y-auto custom-scrollbar">
                    <fieldset
                        className={`flex flex-col w-full gap-10`}
                        disabled={false}
                    >
                        <PurchaseLotPositionInfo
                            theme={theme}
                            purchaseAction={
                                purchase_action == 'edit' ||
                                purchase?.purchase_stage == 'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            purchaseLotGuid={purchaseLot?.guid}
                            purchaseLotItems={purchaseLotItems}
                            currencyList={currencyList}
                            purchaseLot={purchaseLot}
                            isLoadingPurchaseLotItems={purchaseLotItemsLoading}
                            orgData={orgData}
                        />
                    </fieldset>
                </div>
            </div>

            <Footer
                theme={theme}
                purchaseAction={purchase_action == 'edit' ? true : false}
                purchaseLotGuid={purchaseLot?.guid}
                purchaseLot={purchaseLot}
                purchase={purchase ?? ({} as Purchase)}
                user_guid={user?.object.user_id}
                purchase_user_guid={purchase?.user_guid ?? ''}
                purchase_supervisor_user_guid={
                    purchase?.supervisor_user_guid ?? ''
                }
                role={isUserRoleName}
                stage={purchase?.purchase_stage ?? ''}
                status={purchase?.purchase_status ?? ''}
            />
        </div>
    )
}

export default PurchaseLotEditPage
